import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MergeIcon from '@mui/icons-material/MergeType';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import NoteIcon from '@mui/icons-material/Note';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import BadgeIcon from '@mui/icons-material/Badge';
import SearchIcon from '@mui/icons-material/Search';
import API from '../../utils/Axios.utils';
import {
  useDuplicatePairs,
  useTriggerScan,
  useMergeCluster,
} from '../../hooks/useDuplicateResolution';

// ---------------------------------------------------------------------------
// Helper: format date
// ---------------------------------------------------------------------------
const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};

// ---------------------------------------------------------------------------
// Helper: format status
// ---------------------------------------------------------------------------
const parseStatusLabel = (status) => {
  if (!status) return 'New';
  return status
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

// ---------------------------------------------------------------------------
// Helper: check if genuine document is uploaded
// ---------------------------------------------------------------------------
const isDocUploaded = (doc, s3Key) => {
  if (s3Key && typeof s3Key === 'string') {
    const s = s3Key.trim().toLowerCase();
    if (s && s !== 'null' && s !== 'undefined' && s !== 'none' && s !== 'n/a') {
      return true;
    }
  }
  if (!doc || typeof doc !== 'string') return false;
  const d = doc.trim().toLowerCase();
  if (!d || d === 'null' || d === 'undefined' || d === 'none' || d === 'n/a' || d === 'talent engine' || d === 'talent_engine') {
    return false;
  }
  // Exclude plain 12-digit Aadhaar numbers or 10-char PAN numbers that aren't file names
  if (/^\d{12}$/.test(d)) return false;
  if (/^[a-z]{5}\d{4}[a-z]$/i.test(d)) return false;

  // Must have a valid document extension or path/URL
  const hasFileExt = /\.(pdf|docx?|jpe?g|png|webp|svg)$/i.test(d);
  const isPathOrUrl = d.startsWith('http://') || d.startsWith('https://') || d.includes('/') || d.includes('\\');
  return hasFileExt || isPathOrUrl;
};

// ---------------------------------------------------------------------------
// Cluster Card
// ---------------------------------------------------------------------------
const DuplicateClusterCard = ({ cluster, onMerge, merging }) => {
  const { clusterId, candidates } = cluster;
  const navigate = useNavigate();
  const location = useLocation();
  const [chosenPrimary, setChosenPrimary] = useState(candidates[0]?.profile_id);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [noteContent, setNoteContent] = useState('');

  const primaryCandidate = candidates.find(c => c.profile_id === chosenPrimary) || candidates[0];

  const handleOpenNote = (e, p) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    let combinedNotes = [];
    if (p.important_note && p.important_note.toLowerCase() !== 'no notes provided') {
      combinedNotes.push(`Recruiter Note: ${p.important_note}`);
    }
    if (p.interview_feedback && p.interview_feedback.toLowerCase() !== 'null') {
      combinedNotes.push(`Interview Feedback: ${p.interview_feedback}`);
    }
    if (p.profile_feedback && p.profile_feedback.toLowerCase() !== 'null') {
      combinedNotes.push(`Profile Feedback: ${p.profile_feedback}`);
    }
    
    setNoteContent(combinedNotes.length > 0 ? combinedNotes.join('\n\n') : 'No notes provided');
    setNoteDialogOpen(true);
  };

  const [docLoading, setDocLoading] = useState(false);

  const handleViewDocument = async (e, p, type) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (docLoading) return;
    setDocLoading(true);
    try {
      const payload = {
        supplierId: p.supplier_id,
        coreSkillId: p.coreSkillId,
        job_code: p.job_code,
        email: p.email,
      };
      const res = await API.post(`/api/profile/candidate_document`, payload);
      
      const docData = res?.data?.data || res?.data || {};
      let targetUrl = docData.url;
      if (type === 'aadhar') targetUrl = docData.aadharUrl;
      if (type === 'pan') targetUrl = docData.panUrl;

      if (targetUrl && (targetUrl.startsWith('http://') || targetUrl.startsWith('https://'))) {
        navigate('/candidate_data/viewFile', {
          state: { fileUrl: targetUrl, from: location.pathname + location.search },
        });
      } else {
        alert("Document file URL is not available or could not be generated");
      }
    } catch (error) {
      console.error("Error fetching document URL:", error);
      alert("Error fetching document");
    } finally {
      setDocLoading(false);
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        mb: 3,
        borderRadius: 2,
        border: '1px solid #e0e0e0',
        overflow: 'hidden',
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {primaryCandidate?.candidate_name || 'Unknown Candidate'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {primaryCandidate?.email || 'No Email'} &bull; {primaryCandidate?.mobile || 'No Mobile'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`${candidates.length} Profiles Found`}
            size="small"
            sx={{
              bgcolor: '#d32f2f',
              color: '#fff',
              fontWeight: 700,
            }}
          />
        </Box>
      </Box>

      {/* Profiles table */}
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#fafafa' }}>
            <TableCell sx={{ fontWeight: 700 }}>Primary</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Profile ID</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Source / Job Code</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Round / Status</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Applied On</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {candidates.map((p) => (
            <TableRow
              key={p.profile_id}
              selected={chosenPrimary === p.profile_id}
              sx={{ cursor: 'pointer' }}
              onClick={() => setChosenPrimary(p.profile_id)}
            >
              <TableCell>
                <input
                  type="radio"
                  name={`primary-${clusterId}`}
                  checked={chosenPrimary === p.profile_id}
                  onChange={() => setChosenPrimary(p.profile_id)}
                  style={{ accentColor: '#2777AA' }}
                />
              </TableCell>
              <TableCell>#{p.profile_id}</TableCell>
              <TableCell>
                {p.supplierInfo?.supplier_org_name || p.source_portal || 'Direct Portal'}
                <br />
                <Typography variant="caption" color="text.secondary">{p.job_code || '—'}</Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={parseStatusLabel(p.detailed_status || p.candidate_status || 'New')}
                  size="small"
                  variant="outlined"
                  sx={{ 
                    textTransform: 'capitalize',
                    bgcolor: '#f5f5f5',
                    color: 'text.primary',
                    borderColor: '#ccc'
                  }}
                />
              </TableCell>
              <TableCell sx={{ verticalAlign: 'middle', minWidth: 200 }}>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                  {isDocUploaded(p.profile_path, p.s3Key_profile_path) && (
                    <Tooltip title="View Resume">
                      <Chip 
                        icon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />} 
                        label="Resume" 
                        size="small" 
                        color="primary" 
                        variant="outlined" 
                        clickable 
                        onClick={(e) => handleViewDocument(e, p, 'resume')} 
                        sx={{ height: 26, fontSize: '0.75rem', fontWeight: 600, borderRadius: '4px' }}
                      />
                    </Tooltip>
                  )}
                  {(() => {
                    const hasNotes = 
                      (p.important_note && p.important_note.toLowerCase() !== 'no notes provided' && p.important_note.toLowerCase() !== 'null') ||
                      (p.interview_feedback && p.interview_feedback.toLowerCase() !== 'null') ||
                      (p.profile_feedback && p.profile_feedback.toLowerCase() !== 'null');
                    
                    if (!hasNotes) return null;
                    
                    return (
                      <Tooltip title="View Note">
                        <Chip 
                          icon={<NoteIcon sx={{ fontSize: 16 }} />} 
                          label="Note" 
                          size="small" 
                          color="secondary" 
                          variant="outlined" 
                          clickable 
                          onClick={(e) => handleOpenNote(e, p)} 
                          sx={{ height: 26, fontSize: '0.75rem', fontWeight: 600, borderRadius: '4px' }}
                        />
                      </Tooltip>
                    );
                  })()}
                  {isDocUploaded(p.aadhar_doc, p.s3Key_aadhar_doc) && (
                    <Tooltip title="View Aadhaar Document">
                      <Chip 
                        icon={<BadgeIcon sx={{ fontSize: 16 }} />} 
                        label="Aadhaar" 
                        size="small" 
                        color="success" 
                        variant="outlined" 
                        clickable 
                        onClick={(e) => handleViewDocument(e, p, 'aadhar')} 
                        sx={{ height: 26, fontSize: '0.75rem', fontWeight: 600, borderRadius: '4px' }}
                      />
                    </Tooltip>
                  )}
                  {isDocUploaded(p.pan_doc, p.s3Key_pan_doc) && (
                    <Tooltip title="View PAN Document">
                      <Chip 
                        icon={<BadgeIcon sx={{ fontSize: 16 }} />} 
                        label="PAN" 
                        size="small" 
                        color="warning" 
                        variant="outlined" 
                        clickable 
                        onClick={(e) => handleViewDocument(e, p, 'pan')} 
                        sx={{ height: 26, fontSize: '0.75rem', fontWeight: 600, borderRadius: '4px' }}
                      />
                    </Tooltip>
                  )}
                  {!isDocUploaded(p.profile_path, p.s3Key_profile_path) &&
                   !isDocUploaded(p.aadhar_doc, p.s3Key_aadhar_doc) &&
                   !isDocUploaded(p.pan_doc, p.s3Key_pan_doc) &&
                   !((p.important_note && p.important_note.toLowerCase() !== 'no notes provided' && p.important_note.toLowerCase() !== 'null') ||
                     (p.interview_feedback && p.interview_feedback.toLowerCase() !== 'null') ||
                     (p.profile_feedback && p.profile_feedback.toLowerCase() !== 'null')) && (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>{fmtDate(p.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, p: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Select the best profile. Missing documents from others will be copied to the primary profile.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<MergeIcon />}
          disabled={merging || !chosenPrimary}
          onClick={() => {
            const secondaryIds = candidates.map(c => c.profile_id).filter(id => id !== chosenPrimary);
            onMerge(clusterId, chosenPrimary, secondaryIds);
          }}
          sx={{ textTransform: 'none', bgcolor: '#2777AA', '&:hover': { bgcolor: '#1a5a8a' } }}
        >
          {merging ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Merge All'}
        </Button>
      </Box>

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onClose={() => setNoteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Recruiter Note</DialogTitle>
        <DialogContent>
          <DialogContentText>{noteContent}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// Main Tab Component
// ---------------------------------------------------------------------------
const DuplicateResolutionTab = () => {
  const { data, isPending, isError, error } = useDuplicatePairs();
  const { mutate: triggerScan, isPending: scanning } = useTriggerScan();
  const { mutate: mergeCluster, isPending: mergingCluster, variables: mergeClusterVars } = useMergeCluster();
  const [searchQuery, setSearchQuery] = useState('');
  const [mergedClusterIds, setMergedClusterIds] = useState(new Set());

  const rawClusters = data?.clusters || [];

  // Sort clusters so newly added candidates appear ON TOP
  const sortedClusters = [...rawClusters].sort((a, b) => {
    const timeA = Math.max(...(a.candidates || []).map(c => new Date(c.createdAt || 0).getTime()));
    const timeB = Math.max(...(b.candidates || []).map(c => new Date(c.createdAt || 0).getTime()));
    if (timeB !== timeA) return timeB - timeA;
    const idA = Math.max(...(a.candidates || []).map(c => c.profile_id || 0));
    const idB = Math.max(...(b.candidates || []).map(c => c.profile_id || 0));
    return idB - idA;
  });

  // Filter out any clusters that were just merged (disappears card immediately)
  const activeClusters = sortedClusters.filter(c => !mergedClusterIds.has(c.clusterId));
  
  const filteredClusters = activeClusters.filter(cluster => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return cluster.candidates.some(c => 
      (c.candidate_name && c.candidate_name.toLowerCase().includes(q)) ||
      (c.email && c.email.toLowerCase().includes(q)) ||
      (c.mobile && c.mobile.toLowerCase().includes(q)) ||
      (c.job_code && c.job_code.toLowerCase().includes(q))
    );
  });

  const handleMerge = (clusterId, primaryProfileId, secondaryProfileIds) => {
    // Immediately remove card from UI without delay
    setMergedClusterIds(prev => new Set(prev).add(clusterId));
    mergeCluster({ clusterId, primaryProfileId, secondaryProfileIds });
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Duplicate Resolution
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="Search duplicates..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ width: 250, bgcolor: '#fff' }}
          />
          <Tooltip title="Scan all candidates for new duplicates">
            <Button
              variant="outlined"
              size="small"
              startIcon={scanning ? <CircularProgress size={14} /> : <RefreshIcon />}
              disabled={scanning}
              onClick={() => triggerScan()}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0, minWidth: 'max-content' }}
            >
              {scanning ? 'Scanning…' : 'Scan Now'}
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Alert banner */}
      {filteredClusters.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Automated Duplicate Detection</strong> — We found{' '}
          <strong>{filteredClusters.length}</strong> duplicate candidate group
          {filteredClusters.length !== 1 ? 's' : ''}. Please review and merge them.
        </Alert>
      )}

      {/* Loading state */}
      {isPending && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state */}
      {isError && (
        <Alert severity="error" icon={<ErrorOutlineIcon />}>
          {error?.response?.data?.message || 'Failed to load duplicates. Please try again.'}
        </Alert>
      )}

      {/* Empty state */}
      {!isPending && !isError && filteredClusters.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#4caf50', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            {searchQuery ? 'No matching candidates found' : 'No duplicate candidates found!'}
          </Typography>
          {!searchQuery && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Automatic scan complete. All candidates are unique.
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            The scan compares candidates on the same job by Aadhaar, mobile and email.
          </Typography>
        </Box>
      )}

      {/* Cluster cards */}
      {!isPending &&
        filteredClusters.map((cluster) => (
          <DuplicateClusterCard
            key={cluster.clusterId}
            cluster={cluster}
            merging={mergingCluster && mergeClusterVars?.clusterId === cluster.clusterId}
            onMerge={handleMerge}
          />
        ))}
    </Box>
  );
};

export default DuplicateResolutionTab;
